import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Gift, X } from 'lucide-react';
import { api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { Heart, Plus, Search, Sparkles, Users } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import EmptyState from '@/components/EmptyState';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function CollaborativeWishlist() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dynamic OG so shared collaborative-wishlist links preview nicely.
  const owner = data?.owner_name || data?.wishlist?.owner_name || 'A friend';
  const wname = data?.wishlist?.name || 'wishlist';
  useDocumentMeta({
    title: data
      ? `Add gifts to ${owner}'s ${wname}, Mementa`
      : 'Shared Wishlist, Mementa',
    description: data
      ? `${owner} invited you to add gift ideas to their ${wname} on Mementa.`
      : "You've been invited to collaborate on a wishlist on Mementa.",
  });

  // Add gift state
  const [collaboratorName, setCollaboratorName] = useState(() => localStorage.getItem('collab_name') || '');
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [adding, setAdding] = useState(null); // product_id currently being added

  const fetchWishlist = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/wishlists/collaborative/${shareToken}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      logger.error(err);
      setError(err?.response?.data?.detail || 'Wishlist not found or sharing is disabled.');
    } finally {
      setLoading(false);
    }
  }, [shareToken]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const loadProducts = async () => {
    setProductsLoading(true);
    try {
      const res = await api.get('/products');
      setProducts(res.data || []);
    } catch (err) {
      logger.error(err);
      toast.error('Could not load products');
    } finally {
      setProductsLoading(false);
    }
  };

  const handleOpenAdd = async () => {
    setShowAddPanel(true);
    if (products.length === 0) await loadProducts();
  };

  const handleAddProduct = async (product) => {
    if (!collaboratorName.trim()) {
      toast.error('Please enter your name first');
      return;
    }
    setAdding(product.id);
    try {
      localStorage.setItem('collab_name', collaboratorName.trim());
      await api.post(`/wishlists/collaborative/${shareToken}/products`, {
        product_id: product.id,
        notes: '',
        added_by_name: collaboratorName.trim()
      });
      toast.success(`Added "${product.name}" to the wishlist`);
      await fetchWishlist();
    } catch (err) {
      logger.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to add gift');
    } finally {
      setAdding(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9FAFB' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9E6752] mx-auto" />
          <p className="mt-4 text-gray-600">Loading collaborative wishlist...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#F9FAFB' }}>
        <div data-testid="collab-wishlist-error" className="bg-white rounded-2xl p-8 text-center max-w-md w-full shadow">
          <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: '#9E6752' }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: '#20212B' }}>Wishlist unavailable</h2>
          <p className="text-sm mb-4" style={{ color: '#73766A' }}>{error || 'Try asking the owner for a fresh link.'}</p>
          <Link to="/"><Button style={{ background: '#9E6752', color: 'white' }}>Go to Mementa</Button></Link>
        </div>
      </div>
    );
  }

  const { wishlist, gifts, can_edit } = data;
  const filteredProducts = products.filter(p => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      (p.tags || []).some(t => t.toLowerCase().includes(q))
    );
  });

  return (
    <div data-testid="collaborative-wishlist-page" className="min-h-screen page-enter" style={{ background: '#F9FAFB' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div
          className="rounded-2xl p-6 sm:p-8 mb-8 relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${wishlist.color} 0%, ${wishlist.color}dd 100%)` }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative z-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.95)' }}>
                <Heart className="w-6 h-6" style={{ color: wishlist.color }} />
              </div>
              <div>
                <h1
                  data-testid="collab-wishlist-title"
                  className="text-2xl sm:text-3xl font-bold"
                  style={{ fontFamily: 'Playfair Display, serif', color: 'white' }}
                >
                  {wishlist.name}
                </h1>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  Collaborative wishlist · {gifts.length} {gifts.length === 1 ? 'item' : 'items'}
                </p>
              </div>
            </div>
            <div
              className="px-3 py-2 rounded-full flex items-center gap-2 text-sm self-start sm:self-auto"
              style={{ background: 'rgba(255,255,255,0.95)', color: '#20212B' }}
            >
              <Users className="w-4 h-4" style={{ color: wishlist.color }} />
              <span>{can_edit ? 'You can add gifts' : 'View only'}</span>
            </div>
          </div>
        </div>

        {/* Add gift toolbar (edit mode only) */}
        {can_edit && (
          <div className="bg-white rounded-2xl p-5 mb-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#534145' }}>Your name (so the owner knows who added it)</label>
                <input
                  data-testid="collab-name-input"
                  value={collaboratorName}
                  onChange={(e) => setCollaboratorName(e.target.value)}
                  placeholder="e.g. Alex"
                  className="w-full p-2.5 rounded-lg border text-sm"
                  style={{ borderColor: '#E5E7EB' }}
                />
              </div>
              <Button
                data-testid="collab-open-add-btn"
                onClick={handleOpenAdd}
                style={{ background: '#9E6752', color: 'white' }}
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Add a gift
              </Button>
            </div>
          </div>
        )}

        {/* Add gift panel */}
        {can_edit && showAddPanel && (
          <div data-testid="collab-add-panel" className="bg-white rounded-2xl p-5 mb-8 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" style={{ color: '#9E6752' }} />
                <h3 className="text-base font-semibold" style={{ color: '#20212B' }}>Pick a gift to add</h3>
              </div>
              <button
                data-testid="collab-close-add-btn"
                onClick={() => setShowAddPanel(false)}
                className="p-1.5 rounded-full hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9CA3AF' }} />
              <input
                data-testid="collab-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-9 p-2.5 rounded-lg border text-sm"
                style={{ borderColor: '#E5E7EB' }}
              />
            </div>
            {productsLoading ? (
              <div className="text-center py-6 text-sm" style={{ color: '#73766A' }}>Loading products...</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[480px] overflow-y-auto pr-1" style={{ overscrollBehavior: 'contain' }}>
                {filteredProducts.slice(0, 60).map(p => (
                  <button
                    key={p.id}
                    data-testid={`collab-add-product-${p.id}`}
                    onClick={() => handleAddProduct(p)}
                    disabled={adding === p.id || !collaboratorName.trim()}
                    className="text-left rounded-xl border p-2 transition-all hover:scale-[1.02] disabled:opacity-50"
                    style={{ borderColor: '#E5E7EB', background: '#FAFAFA' }}
                  >
                    {p.image_url && (
                      <div className="aspect-square rounded-lg overflow-hidden bg-white mb-2">
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <p className="text-xs font-medium line-clamp-2" style={{ color: '#20212B' }}>{p.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#73766A' }}>${p.price}</p>
                    <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: '#9E6752' }}>
                      <Plus className="w-3 h-3" />
                      Add
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Gifts grid */}
        {gifts.length === 0 ? (
          <EmptyState
            icon={Gift}
            title="No gifts yet"
            description={
              can_edit
                ? 'Be the first to add a gift, anything you save here is visible to everyone with the link.'
                : 'Check back later, the owner is still curating this list.'
            }
            compact
            testId="collab-wishlist-empty"
          />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-5 sm:gap-x-4 sm:gap-y-6">
            {gifts.map(item => (
              <div key={item.id} className="relative">
                <ProductCard
                  product={item.product}
                  user={null}
                  hideHeartButton
                />
                {item.added_by_name && (
                  <div
                    className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-sm"
                    style={{ background: 'white', color: '#534145' }}
                  >
                    <Users className="w-3 h-3" style={{ color: '#9E6752' }} />
                    <span>by {item.added_by_name}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 text-center text-xs" style={{ color: '#73766A' }}>
          Powered by <Link to="/" className="font-semibold" style={{ color: '#9E6752' }}>Mementa</Link>
        </div>
      </div>
    </div>
  );
}
